import React, { memo } from 'react'
import ControlledRadioGroupInput from 'transfix-form/components/ControlledRadioGroupInput'
import ControlledTextInput from 'transfix-form/components/ControlledTextInput'
import { FormIf } from 'transfix-form/components/FormIf'
import { FormHelperText } from 'transfix-ui/core'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { IEquipmentAttributeTypes } from 'types/graphqlTypes'
import { useCreateShipmentFormContext } from 'apps/marketplace/pages/create-shipment/context'
import { TReeferSchema } from 'apps/marketplace/pages/create-shipment/schema/EquipmentDetailsSchema/EquipmentReeferSchema'
import { getEquipmentAttributeErrorMessage } from 'apps/marketplace/pages/create-shipment/utils'
import { IReeferTempControlTypes } from 'apps/marketplace/types/equipment'
import { REEFER_TEMP_CONTROL_OPTIONS } from 'apps/marketplace/utils/constants/graphqlEquipment'
import { TReeferSchemaErrors } from '../types'
import { useReeferEquipmentFormSectionStyles } from '../useReeferEquipmentFormSectionStyles'

export interface IReeferTemperatureFormSectionProps {}

const ReeferTemperatureFormSection = memo<IReeferTemperatureFormSectionProps>(() => {
  const classes = useReeferEquipmentFormSectionStyles()
  /**
   * Form Context
   */
  const { control, errors } = useCreateShipmentFormContext()
  const { equipmentAttributes } = errors || {}
  const equipmentAttributesErrors = equipmentAttributes as TReeferSchemaErrors

  const getErrorMessage = (keyName: keyof TReeferSchemaErrors) => {
    return getEquipmentAttributeErrorMessage<TReeferSchema>(equipmentAttributesErrors, keyName)
  }

  const tempControlTypeErrorMessage = getErrorMessage(IEquipmentAttributeTypes.temp_control_type)

  return (
    <div>
      <div>
        <Typography variant='subtitle1'>Temperature (&deg;F)</Typography>
        <div className={classes.radioContainer}>
          <ControlledRadioGroupInput
            control={control}
            name={`equipmentAttributes.${IEquipmentAttributeTypes.temp_control_type}`}
            options={REEFER_TEMP_CONTROL_OPTIONS}
            RadioProps={{
              dataTest: 'temp-control-type-field',
            }}
          />
          {tempControlTypeErrorMessage && (
            <FormHelperText className='Mui-error'>{tempControlTypeErrorMessage}</FormHelperText>
          )}
        </div>
      </div>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ControlledTextInput
            control={control}
            name={`equipmentAttributes.${IEquipmentAttributeTypes.precool}`}
            TextInputProps={{
              label: 'Precool',
              placeholder: '00 °F',
              dataTest: 'precool-field',
              type: 'number',
              error: !!getErrorMessage(IEquipmentAttributeTypes.precool),
              validationMessage: getErrorMessage(IEquipmentAttributeTypes.precool),
              required: true,
            }}
          />
        </Grid>
        <FormIf
          control={control}
          name={`equipmentAttributes.${IEquipmentAttributeTypes.temp_control_type}`}
          when={IReeferTempControlTypes.fixed}>
          <Grid item xs={12} md={4}>
            <ControlledTextInput
              control={control}
              name={`equipmentAttributes.${IEquipmentAttributeTypes.fixed_temp_control}`}
              TextInputProps={{
                label: 'Temp',
                placeholder: '00 °F',
                dataTest: 'fixed-temp-control-field',
                type: 'number',
                error: !!getErrorMessage(IEquipmentAttributeTypes.fixed_temp_control),
                validationMessage: getErrorMessage(IEquipmentAttributeTypes.fixed_temp_control),
                required: true,
              }}
            />
          </Grid>
        </FormIf>
        <FormIf
          control={control}
          name={`equipmentAttributes.${IEquipmentAttributeTypes.temp_control_type}`}
          when={IReeferTempControlTypes.range}>
          <>
            <Grid item xs={12} md={4}>
              <ControlledTextInput
                control={control}
                name={`equipmentAttributes.${IEquipmentAttributeTypes.min_temp_control}`}
                TextInputProps={{
                  label: 'Min',
                  placeholder: '00 °F',
                  dataTest: 'min-temp-control-field',
                  type: 'number',
                  error: !!getErrorMessage(IEquipmentAttributeTypes.min_temp_control),
                  validationMessage: getErrorMessage(IEquipmentAttributeTypes.min_temp_control),
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ControlledTextInput
                control={control}
                name={`equipmentAttributes.${IEquipmentAttributeTypes.max_temp_control}`}
                TextInputProps={{
                  label: 'Max',
                  placeholder: '00 °F',
                  dataTest: 'max-temp-control-field',
                  type: 'number',
                  error: !!getErrorMessage(IEquipmentAttributeTypes.max_temp_control),
                  validationMessage: getErrorMessage(IEquipmentAttributeTypes.max_temp_control),
                  required: true,
                }}
              />
            </Grid>
          </>
        </FormIf>
      </Grid>
    </div>
  )
})

export default ReeferTemperatureFormSection
